import {useMemo} from 'react';

import {getRenderedGridRows} from 'studies/typed/shared/procedure-runs/conditions/conditionGroupGridRows';
import {ExperimentMethod} from 'studies/typed/shared/procedure-runs/design-state/ExperimentalInputs';
import {ConditionGroup} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';

import {
  getMissingReplicateFlowchartEdgeSpecs,
  ReplicateFlowchartAutomappingNode,
} from './procedureRunDesignFlowchartAutomapping';
import {
  canDrawOutgoingEdgeFromStatus,
  getNodeConnectionCapabilities,
} from './procedureRunDesignFlowchartConnectionUtils';
import {FlowchartEdgeSpec, StatusByNodeId} from './ProcedureRunDesignFlowchartLayoutUtils';
import {getProcedureRunDesignFlowchartNodeId} from './ProcedureRunDesignFlowchartNodeIdUtils';

export interface GetAutomappedEdgesForProcedureRunArgs {
  isEnabled: boolean;
  conditions: Array<ConditionGroup>;
  methods: Array<ExperimentMethod>;
  activeMethodId: string | null;
  statusByNodeId: StatusByNodeId;
  existingEdgeSpecs: Array<FlowchartEdgeSpec>;
  removedNodeIds: ReadonlySet<string>;
}

/** Derives new edges for eligible unconnected nodes. */
export function getAutomappedEdgesForProcedureRun({
  isEnabled,
  conditions,
  methods,
  activeMethodId,
  statusByNodeId,
  existingEdgeSpecs,
  removedNodeIds,
}: GetAutomappedEdgesForProcedureRunArgs): Array<FlowchartEdgeSpec> {
  if (!isEnabled || activeMethodId == null) {
    return [];
  }
  const activeMethodIndex = methods.findIndex((method) => method.id === activeMethodId);
  if (activeMethodIndex <= 0) {
    return [];
  }

  const sourceMethodId = methods[activeMethodIndex - 1].id;
  // We only care about the method edge between the immediately upstream and active unit op, since only that
  // layer is editable.
  const directMethodLinks = [{sourceMethodId, targetMethodId: activeMethodId}];

  const {capabilitiesByNodeId} = getNodeConnectionCapabilities({
    conditions,
    methods,
    edges: [],
    activeMethodId,
    removedNodeIds,
  });
  const nodesByMethodId = new Map<string, Array<ReplicateFlowchartAutomappingNode>>([
    [sourceMethodId, []],
    [activeMethodId, []],
  ]);
  // showArchived is false to exclude archived nodes
  getRenderedGridRows(conditions, methods, false).forEach(({conditionGroupIndex, replicateIndex, cells}) => {
    cells.forEach(({methodId}) => {
      const nodes = nodesByMethodId.get(methodId);
      if (nodes == null) {
        return;
      }
      const nodeId = getProcedureRunDesignFlowchartNodeId({
        conditionIndex: conditionGroupIndex,
        replicateIndex,
        methodId,
      });
      if (!removedNodeIds.has(nodeId)) {
        const capabilities = capabilitiesByNodeId.get(nodeId);
        nodes.push({
          nodeId,
          isSourceEligible:
            capabilities?.source === true && canDrawOutgoingEdgeFromStatus(statusByNodeId.get(nodeId)),
          isTargetEligible: capabilities?.target === true,
        });
      }
    });
  });

  const sourceNodeIdSet = new Set(nodesByMethodId.get(sourceMethodId)?.map(({nodeId}) => nodeId) ?? []);
  const targetNodeIdSet = new Set(nodesByMethodId.get(activeMethodId)?.map(({nodeId}) => nodeId) ?? []);
  // Filter for existing edges going between the immediately upstream and active unit op layer
  const existingEdgeSpecsForActiveLayer = existingEdgeSpecs.filter(
    ({sourceNodeId, targetNodeId}) => sourceNodeIdSet.has(sourceNodeId) && targetNodeIdSet.has(targetNodeId)
  );

  return getMissingReplicateFlowchartEdgeSpecs({
    directMethodLinks,
    nodesByMethodId,
    existingEdgeSpecs: existingEdgeSpecsForActiveLayer,
  });
}

export function useGetAutomappedEdgesForProcedureRun({
  activeMethodId,
  conditions,
  existingEdgeSpecs,
  isEnabled,
  methods,
  removedNodeIds,
  statusByNodeId,
}: GetAutomappedEdgesForProcedureRunArgs): Array<FlowchartEdgeSpec> {
  return useMemo(
    () =>
      getAutomappedEdgesForProcedureRun({
        activeMethodId,
        conditions,
        existingEdgeSpecs,
        isEnabled,
        methods,
        removedNodeIds,
        statusByNodeId,
      }),
    [activeMethodId, conditions, existingEdgeSpecs, isEnabled, methods, removedNodeIds, statusByNodeId]
  );
}
